/**
 * Nutrition — matches the attached mobile mockup:
 *  ┌─────────────────────────────────────────┐
 *  │  ← Nutrition                     ⚙       │
 *  │  ◀ Today Sunday, 2 August  ▶            │
 *  │  ┌───────────────────────────────────┐   │
 *  │  │  (turquoise ring)                 │   │
 *  │  │   2000 KCAL REMAINING             │   │
 *  │  │   0/2000 kcal                     │   │
 *  │  │   DEFAULT GOAL · TAP TO PERSONALISE│   │
 *  │  └───────────────────────────────────┘   │
 *  │  Protein • Carbs • Fat (mini bars)       │
 *  │  HYDRATION (8 glasses)                   │
 *  │  Breakfast / Lunch / Dinner / Snacks     │
 *  │  Nutrition insight card                  │
 *  └─────────────────────────────────────────┘
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Settings, Camera, Pencil, Sparkles, Coffee, Utensils, Moon, Cookie, Scan, Image as ImageIcon } from "lucide-react";
import { apiClient } from "../../services/api";
import HydrationRow from "../../components/home/HydrationRow";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";

const RING_COLOUR = "#0FB5A6";        // deep teal — number + label
const RING_TRACK = "#B5EAE6";         // pale aqua — unfilled ring
const CARD_AQUA_BG = "#DCF6F2";       // pale mint — calorie card background
const CARD_AQUA_BG_2 = "#C9EFEA";     // slightly deeper mint — decorative circles
const MACRO_TEAL = "#22C7BB";         // protein/carbs bar
const ACCENT_PURPLE = "#8B7BE8";      // fat bar + HYDRATION label + NUTRITION INSIGHT

function CalorieRing({ consumed, goal }) {
  const pct = Math.max(0, Math.min(1, consumed / Math.max(1, goal)));
  const size = 210;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: "relative", width: size, height: size, margin: "0 auto" }} data-testid="nutrition-calorie-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={RING_TRACK} strokeWidth={stroke} />
        <circle
          cx={size/2} cy={size/2} r={r} fill="none"
          stroke={RING_COLOUR} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
          style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%", transition: "stroke_dashoffset 0.6s ease" }}
        />
        {/* Small dot at 12 o'clock (spec detail) */}
        <circle cx={size/2} cy={stroke/2 + 1} r={6} fill={RING_COLOUR} />
      </svg>
      <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
        <div style={{ fontFamily:"Barlow Condensed, sans-serif", fontWeight:900, fontSize:52, lineHeight:1, color:RING_COLOUR }}>
          {Math.max(0, goal - consumed).toLocaleString("en-IN")}
        </div>
        <div style={{ fontFamily:"Barlow, sans-serif", fontSize:11, letterSpacing:"0.18em", color:RING_COLOUR, marginTop:6, textTransform:"uppercase", fontWeight:700 }}>
          Kcal remaining
        </div>
        <div style={{ fontFamily:"Barlow, sans-serif", fontSize:13, color:"#4A4845", marginTop:6 }}>
          {consumed.toLocaleString("en-IN")} / {goal.toLocaleString("en-IN")} kcal
        </div>
      </div>
    </div>
  );
}

function MacroBar({ label, consumed, goal, tint }) {
  const pct = Math.max(0, Math.min(1, (consumed || 0) / Math.max(1, goal || 1)));
  return (
    <div className="bg-white rounded-2xl p-3 border border-line2" style={{ boxShadow:"var(--shadow-soft)", flex:1, minWidth:0 }}>
      <div style={{ fontFamily:"Barlow, sans-serif", fontSize:10, letterSpacing:"0.16em", textTransform:"uppercase", color:"#8A8884", fontWeight:700 }}>{label}</div>
      <div style={{ height:4, borderRadius:100, background:"#E9EDEA", marginTop:8, overflow:"hidden" }}>
        <div style={{ width:`${pct*100}%`, height:"100%", background:tint, transition:"width 0.6s ease" }} />
      </div>
      <div style={{ fontFamily:"Barlow, sans-serif", fontSize:13, color:"#0C0C0B", marginTop:8, fontWeight:600 }}>
        {Math.round(consumed || 0)}<span style={{ color:"#8A8884", fontWeight:500 }}>g / {Math.round(goal || 0)}g</span>
      </div>
    </div>
  );
}

function MealCard({ meal, kcal, onAdd, testid, Icon = Utensils }) {
  return (
    <div className="bg-white rounded-2xl border border-line2 overflow-hidden" style={{ boxShadow:"var(--shadow-soft)" }} data-testid={testid}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:CARD_AQUA_BG, display:"grid", placeItems:"center" }}>
            <Icon size={18} color={RING_COLOUR} />
          </div>
          <div style={{ fontFamily:"Barlow Condensed, sans-serif", fontWeight:700, fontSize:18, color:"#0C0C0B" }}>{meal}</div>
        </div>
        <div style={{ fontFamily:"Barlow, sans-serif", fontSize:12, color:"#8A8884" }}>{kcal} kcal</div>
      </div>
      <button
        type="button"
        onClick={onAdd}
        data-testid={`add-${meal.toLowerCase()}`}
        style={{
          display:"flex", alignItems:"center", gap:6,
          width:"100%", padding:"12px 16px",
          background:"#FFFFFF", border:0, borderTop:"1px solid var(--line2, #EEEAE1)",
          color:RING_COLOUR, fontFamily:"Barlow, sans-serif", fontWeight:600, fontSize:14,
          cursor:"pointer", textAlign:"left"
        }}
      >
        <span style={{ fontSize:18, lineHeight:1 }}>+</span> Add food
      </button>
    </div>
  );
}

export default function Nutrition() {
  const navigate = useNavigate();
  const [goal, setGoal] = useState(null);
  const [today, setToday] = useState(null); // { calories, protein_g, carbs_g, fat_g, meals: {breakfast, lunch, dinner, snacks} }
  const [sheetMeal, setSheetMeal] = useState(null); // opens the "Add to X" sheet
  const [date, setDate] = useState(() => new Date());

  // ISO YYYY-MM-DD for a JS date, in the browser's local zone.
  const isoDay = (d) => {
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 10);
  };
  const todayIso = isoDay(new Date());
  const yesterdayIso = isoDay(new Date(Date.now() - 86_400_000));
  const selectedIso = isoDay(date);
  const isToday = selectedIso === todayIso;
  const isYesterday = selectedIso === yesterdayIso;
  const isFuture = selectedIso > todayIso; // guard

  useEffect(() => {
    apiClient.get("/users/me/calorie-goal").then(r => setGoal(r.data)).catch(() => setGoal({ calorie_goal: 2000, protein_g: 100, carbs_g: 250, fat_g: 65, basis: "fallback" }));
  }, []);

  useEffect(() => {
    setToday(null); // spinner state
    if (isToday) {
      apiClient.get("/nutrition/today")
        .then(r => setToday(r.data))
        .catch(() => setToday({ calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, meals: {}, empty: true }));
    } else {
      // Historical day — /nutrition/logs?date=YYYY-MM-DD returns [log] or [].
      apiClient.get(`/nutrition/logs?date=${selectedIso}`)
        .then((r) => {
          const doc = Array.isArray(r.data) ? r.data[0] : null;
          if (!doc) {
            setToday({ calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, meals: {}, empty: true });
            return;
          }
          const meals_arr = doc.meals || [];
          const totals = { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };
          const slots = { breakfast: { calories: 0 }, lunch: { calories: 0 }, dinner: { calories: 0 }, snacks: { calories: 0 } };
          meals_arr.forEach((m) => {
            totals.calories += Number(m.calories || 0);
            totals.protein_g += Number(m.protein_g || 0);
            totals.carbs_g += Number(m.carbs_g || 0);
            totals.fat_g += Number(m.fat_g || 0);
            const slot = (m.meal_type || "snacks").toLowerCase();
            if (slots[slot]) slots[slot].calories += Number(m.calories || 0);
          });
          setToday({ ...totals, meals: slots, items: meals_arr, empty: meals_arr.length === 0 });
        })
        .catch(() => setToday({ calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, meals: {}, empty: true }));
    }
  }, [selectedIso, isToday]);

  const consumed = today?.calories ?? 0;
  const target = goal?.calorie_goal ?? 2000;
  const dateLabel = useMemo(() => {
    if (isToday) return date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
    if (isYesterday) return date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
    return date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  }, [date, isToday, isYesterday]);

  const shiftDay = (delta) => {
    const next = new Date(date.getTime());
    next.setDate(next.getDate() + delta);
    // Never allow future days.
    if (isoDay(next) > todayIso) return;
    setDate(next);
  };

  const perMeal = (name) => Math.round(today?.meals?.[name.toLowerCase()]?.calories || 0);

  const openAdd = (meal) => setSheetMeal(meal);
  const goScan = () => { const m = sheetMeal?.toLowerCase(); setSheetMeal(null); navigate(`/nutrition/scanner?meal=${m}&mode=scan`); };
  const goBarcode = () => { const m = sheetMeal?.toLowerCase(); setSheetMeal(null); navigate(`/nutrition/scanner?meal=${m}&mode=barcode`); };
  const goGallery = () => { const m = sheetMeal?.toLowerCase(); setSheetMeal(null); navigate(`/nutrition/scanner?meal=${m}&mode=gallery`); };
  const goManual = () => { const m = sheetMeal?.toLowerCase(); setSheetMeal(null); navigate(`/nutrition/scanner?meal=${m}&mode=manual`); };

  return (
    <div className="min-h-screen bg-canvas" data-testid="nutrition-page">
      <TopBar />
      <main style={{ maxWidth: 480, margin: "0 auto", padding: "16px 16px 96px 16px", display:"flex", flexDirection:"column", gap:20 }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <button onClick={() => navigate("/home")} data-testid="nutrition-back" aria-label="Back" style={{ padding:6, background:"transparent", border:0, cursor:"pointer" }}>
            <ChevronLeft size={22} color="#0C0C0B" />
          </button>
          <h1 style={{ fontFamily:"Barlow Condensed, sans-serif", fontWeight:800, fontSize:24, color:"#0C0C0B", margin:0 }}>Nutrition</h1>
          <button onClick={() => navigate("/profile-setup")} data-testid="nutrition-settings" aria-label="Personalise goal" style={{ padding:6, background:"transparent", border:0, cursor:"pointer" }}>
            <Settings size={20} color="#4A4845" />
          </button>
        </div>

        {/* Date selector */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:16 }}>
          <button
            aria-label="Previous day"
            data-testid="nutrition-prev-day"
            onClick={() => shiftDay(-1)}
            style={{ padding:4, background:"transparent", border:0, cursor:"pointer" }}
          >
            <ChevronLeft size={18} color="#0C0C0B" />
          </button>
          <div style={{ textAlign:"center" }} data-testid="nutrition-date-label">
            <div style={{ fontFamily:"Barlow Condensed, sans-serif", fontWeight:700, fontSize:16, color:"#0C0C0B" }}>
              {isToday ? "Today" : isYesterday ? "Yesterday" : ""}
            </div>
            <div style={{ fontFamily:"Barlow, sans-serif", fontSize:12, color:"#8A8884" }}>{dateLabel}</div>
          </div>
          <button
            aria-label="Next day"
            data-testid="nutrition-next-day"
            onClick={() => shiftDay(1)}
            disabled={isFuture || isToday}
            style={{ padding:4, background:"transparent", border:0, cursor: (isFuture || isToday) ? "not-allowed" : "pointer", opacity: (isFuture || isToday) ? 0.3 : 1 }}
          >
            <ChevronRight size={18} color="#0C0C0B" />
          </button>
        </div>

        {/* Empty-day banner — shown only for past days with no food logged */}
        {!isToday && today?.empty && (
          <div
            data-testid="nutrition-empty-day"
            style={{
              padding: "14px 16px",
              background: "#FFFFFF",
              border: "1px dashed #E7E5E1",
              borderRadius: 14,
              textAlign: "center",
              color: "#8A8884",
              fontFamily: "Barlow, sans-serif",
              fontSize: 13,
            }}
          >
            Nothing logged on this day.
          </div>
        )}

        {/* Calorie ring card */}
        <div className="bg-white rounded-2xl p-5 border border-line2" style={{ boxShadow:"var(--shadow-soft)" }}>
          <CalorieRing consumed={consumed} goal={target} />
          <button
            type="button"
            onClick={() => navigate(goal?.basis === "nutritionist_plan" ? "/me/plans" : "/profile-setup")}
            data-testid="nutrition-personalise-goal"
            style={{ display:"block", margin:"12px auto 0", background:"transparent", border:0, cursor:"pointer",
                     fontFamily:"Barlow, sans-serif", fontSize:11, letterSpacing:"0.14em", color: goal?.basis === "nutritionist_plan" ? "#0F766E" : "#8A8884", textTransform:"uppercase", fontWeight: goal?.basis === "nutritionist_plan" ? 800 : 400 }}
          >
            {goal?.basis === "nutritionist_plan"
              ? `Target set by ${goal.set_by || "your nutritionist"} · Tap to view`
              : goal?.basis === "fallback"
              ? "Default goal · Tap to personalise"
              : "Goal · Tap to adjust"}
          </button>
          <div style={{ display:"flex", gap:16, marginTop:20, paddingTop:16, borderTop:"1px solid var(--line2, #EEEAE1)" }}>
            <MacroBar label="Protein" consumed={today?.protein_g} goal={goal?.protein_g} tint="#22C55E" />
            <MacroBar label="Carbs"   consumed={today?.carbs_g}   goal={goal?.carbs_g}   tint="#F5C518" />
            <MacroBar label="Fat"     consumed={today?.fat_g}     goal={goal?.fat_g}     tint="#F97316" />
          </div>
        </div>

        {/* HYDRATION — moved from Home per spec */}
        <section data-testid="nutrition-hydration">
          <div style={{ fontFamily:"Barlow, sans-serif", fontSize:11, letterSpacing:"0.18em", color:"#8A8884", textTransform:"uppercase", marginBottom:8 }}>
            Hydration
          </div>
          <HydrationRow />
        </section>

        {/* Meal cards */}
        <section style={{ display:"flex", flexDirection:"column", gap:10 }} data-testid="nutrition-meals">
          <MealCard meal="Breakfast" kcal={perMeal("breakfast")} onAdd={() => openAdd("Breakfast")} testid="meal-breakfast" Icon={Coffee} />
          <MealCard meal="Lunch"     kcal={perMeal("lunch")}     onAdd={() => openAdd("Lunch")}     testid="meal-lunch"     Icon={Utensils} />
          <MealCard meal="Dinner"    kcal={perMeal("dinner")}    onAdd={() => openAdd("Dinner")}    testid="meal-dinner"    Icon={Moon} />
          <MealCard meal="Snacks"    kcal={perMeal("snacks")}    onAdd={() => openAdd("Snacks")}    testid="meal-snacks"    Icon={Cookie} />
        </section>

        {/* AI Nutrition insight */}
        <button
          type="button"
          onClick={() => navigate("/nutrition/chat")}
          data-testid="nutrition-insight-card"
          className="w-full text-left"
          style={{ background:"#F0EFEC", border:"1px solid var(--line2, #EEEAE1)", borderRadius:16, padding:14, display:"flex", gap:12, cursor:"pointer" }}
        >
          <div style={{ width:40, height:40, borderRadius:12, background:"rgba(15,181,166,0.15)", display:"grid", placeItems:"center", flexShrink:0 }}>
            <Sparkles size={20} color={RING_COLOUR} />
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontFamily:"Barlow Condensed, sans-serif", fontWeight:700, fontSize:16, color:"#0C0C0B" }}>Ask AI Coach</div>
            <div style={{ fontFamily:"Barlow, sans-serif", fontSize:12, color:"#4A4845", marginTop:2 }}>Get instant feedback on today&apos;s nutrition</div>
          </div>
        </button>
      </main>

      {/* Bottom sheet: Add food */}
      {sheetMeal && (
        <div onClick={() => setSheetMeal(null)} data-testid="add-food-sheet"
             style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:70, display:"grid", placeItems:"end" }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ width:"100%", maxWidth:560, margin:"0 auto", background:"#fff", borderRadius:"20px 20px 0 0", padding:24 }}>
            <h3 style={{ fontFamily:"Barlow Condensed, sans-serif", fontWeight:800, fontSize:20, marginBottom:16 }}>Add to {sheetMeal}</h3>
            <button onClick={goScan} data-testid="add-food-scan"
              className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-line2 mb-3 text-left" style={{ background:"#fff", cursor:"pointer" }}>
              <div style={{ width:40, height:40, borderRadius:12, background:"rgba(15,181,166,0.15)", display:"grid", placeItems:"center" }}>
                <Camera size={20} color={RING_COLOUR} />
              </div>
              <div>
                <div style={{ fontFamily:"Barlow Condensed, sans-serif", fontWeight:700, fontSize:16 }}>Scan food</div>
                <div style={{ fontFamily:"Barlow, sans-serif", fontSize:12, color:"#8A8884" }}>Open the camera and snap your meal</div>
              </div>
            </button>
            <button onClick={goBarcode} data-testid="add-food-barcode"
              className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-line2 mb-3 text-left" style={{ background:"#fff", cursor:"pointer" }}>
              <div style={{ width:40, height:40, borderRadius:12, background:"rgba(139,123,232,0.15)", display:"grid", placeItems:"center" }}>
                <Scan size={20} color={ACCENT_PURPLE} />
              </div>
              <div>
                <div style={{ fontFamily:"Barlow Condensed, sans-serif", fontWeight:700, fontSize:16 }}>Scan barcode</div>
                <div style={{ fontFamily:"Barlow, sans-serif", fontSize:12, color:"#8A8884" }}>Look up any packaged product instantly</div>
              </div>
            </button>
            <button onClick={goGallery} data-testid="add-food-gallery"
              className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-line2 mb-3 text-left" style={{ background:"#fff", cursor:"pointer" }}>
              <div style={{ width:40, height:40, borderRadius:12, background:"rgba(34,199,187,0.15)", display:"grid", placeItems:"center" }}>
                <ImageIcon size={20} color={MACRO_TEAL} />
              </div>
              <div>
                <div style={{ fontFamily:"Barlow Condensed, sans-serif", fontWeight:700, fontSize:16 }}>Upload from gallery</div>
                <div style={{ fontFamily:"Barlow, sans-serif", fontSize:12, color:"#8A8884" }}>Pick a saved photo from your device</div>
              </div>
            </button>
            <button onClick={goManual} data-testid="add-food-manual"
              className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-line2 text-left" style={{ background:"#fff", cursor:"pointer" }}>
              <div style={{ width:40, height:40, borderRadius:12, background:"rgba(12,12,11,0.06)", display:"grid", placeItems:"center" }}>
                <Pencil size={20} color="#0C0C0B" />
              </div>
              <div>
                <div style={{ fontFamily:"Barlow Condensed, sans-serif", fontWeight:700, fontSize:16 }}>Log manually</div>
                <div style={{ fontFamily:"Barlow, sans-serif", fontSize:12, color:"#8A8884" }}>Search foods or enter macros</div>
              </div>
            </button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
